<template>
  <div class="invoice-detail-page">
    <CommonPageShell
      :title="headerTitle"
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #left>
        <div class="invoice-detail-header-left">
          <AppBreadcrumb
            mode="manual"
            :items="breadcrumbItems"
            :aria-label="breadcrumbAriaLabel"
            :show-back="true"
            :back-label="breadcrumbBackLabel"
            :fallback-target="{ name: 'simpleNotes' }"
          />

          <div class="app-detail-header invoice-detail-heading-wrap">
            <div class="app-detail-heading invoice-detail-heading-content">
              <div class="invoice-detail-heading-main">
                <h2 class="invoice-detail-heading-title">{{ simpleNoteHeaderTitle }}</h2>
                <span
                  class="app-status-pill app-detail-status-pill"
                  :class="simpleNoteIsCanceled ? 'app-status-inactive' : 'app-status-active'"
                >
                  {{ simpleNoteStatusLabel }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #actions="{ onAction }">
        <div class="app-page-header-actions app-detail-header-actions">
          <button
            v-for="action in primaryHeaderActions"
            :key="action.key"
            class="app-header-btn"
            :class="[
              `app-header-btn--${action.variant || 'primary'}`,
              {
                'invoice-detail-header-btn--icon-only': action.iconOnly,
                'invoice-detail-header-btn--download-bundle': action.key === 'download',
              }
            ]"
            type="button"
            :title="String(action?.title || action?.label || '')"
            @click="handlePrimaryHeaderAction(action, onAction)"
          >
            <i v-if="action.icon" class="fa" :class="action.icon" aria-hidden="true"></i>
            <span v-if="!action.iconOnly">{{ action.label }}</span>
          </button>

          <div v-if="secondaryHeaderActions.length" class="app-detail-more-actions-wrap" ref="moreActionsWrap">
            <button
              type="button"
              class="app-header-btn app-header-btn--ghost app-detail-more-actions-btn"
              :title="text.detail.header.moreActionsTitle"
              @click.stop="toggleMoreActionsMenu"
            >
              <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
            </button>

            <div v-if="showMoreActionsMenu" class="app-detail-more-actions-menu">
              <button
                v-for="action in secondaryHeaderActions"
                :key="action.key"
                type="button"
                :class="{ 'invoice-detail-more-actions-menu__item--critical': action.key === 'cancel' }"
                :title="action.key === 'cancel' ? String(text?.detail?.header?.cancelImportantHint || '').trim() : ''"
                @click="handleSecondaryHeaderAction(action, onAction)"
              >
                <span>{{ action.label }}</span>
              </button>
            </div>
          </div>
        </div>
      </template>

      <template #content>
        <section v-if="!loading && note" class="invoice-detail-content-wrap">
          <AppSectionTabs
            v-model="activeTab"
            :tabs="detailTabs"
            :aria-label="sectionTabsAriaLabel"
          />

          <SimpleNoteDetailTabsPanel
            :active-tab="activeTab"
            :note="note"
            :operation="operationData"
            :concepts="noteConcepts"
            :comments="noteComments"
            :currency="noteCurrency"
            :subtotal="noteSubtotal"
            :total="noteTotal"
            :shipping-refresh-tick="noteShippingRefreshTick"
            @pay-note="openPayNoteModal"
          />
        </section>

        <section v-else-if="loading" class="app-card simple-note-detail-loading">
          {{ text.detail.loading }}
        </section>

        <section v-else class="app-card simple-note-detail-loading">
          {{ text.detail.loadError }}
        </section>
      </template>
    </CommonPageShell>

    <PayModal
      :show="showPayNoteModal"
      :row="payNoteRow"
      :submitting="payNoteSubmitting"
      @close="closePayNoteModal"
      @submit="submitPayNoteModal"
    />

    <SendEmailModal
      context-type="simple_note"
      :context-data="sendEmailContextData"
      :show="showSendEmailModal"
      :submitting="sendEmailModalSubmitting"
      @close="closeSendEmailModal"
      @submit="submitSendEmailModal"
    />

    <ConfirmActionModal
      :show="showCancelConfirmModal"
      tone="danger"
      :loading="cancelNoteSubmitting"
      :title="cancelConfirmTitle"
      :subtitle="cancelConfirmSubtitle"
      :message="cancelConfirmMessage"
      :confirm-text="cancelConfirmButton"
      :cancel-text="cancelConfirmDismiss"
      @cancel="closeCancelConfirmModal"
      @confirm="confirmCancelSimpleNote"
    />
  </div>
</template>

<script>
import AppBreadcrumb from '@/components/common/AppBreadcrumb.vue';
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import CommonPageShell from '@/components/common/CommonPageShell.vue';
import PayModal from '@/components/pay/modal/PayModal.vue';
import SendEmailModal from '@/components/sendEmail/SendEmailModal.vue';
import SimpleNoteDetailTabsPanel from '@/components/simple-notes/detail/SimpleNoteDetailTabsPanel.vue';
import simpleNotesService from '@/services/simpleNotes';
import paymentReceiptsService from '@/services/paymentReceipts';
import { useDomainText } from '@/composables/text/useDomainText';
import { invoiceDetailSendEmailMethods } from '@/composables/send/sendEmailMethods';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractPayload,
} from '@/utils/dataNormalization';

const { catalog: text } = useDomainText('simpleNotes');
const { catalog: sendEmailText } = useDomainText('sendEmail');

export default {
  name: 'SimpleNoteDetailPage',
  components: {
    AppBreadcrumb,
    AppSectionTabs,
    ConfirmActionModal,
    CommonPageShell,
    PayModal,
    SendEmailModal,
    SimpleNoteDetailTabsPanel,
  },
  data() {
    return {
      text,
      sendEmailText,
      loading: false,
      note: null,
      activeTab: 'detail',
      showMoreActionsMenu: false,
      showPayNoteModal: false,
      payNoteSubmitting: false,
      payNoteRow: null,
      noteShippingRefreshTick: 0,
      showSendEmailModal: false,
      sendEmailModalSubmitting: false,
      showCancelConfirmModal: false,
      cancelNoteSubmitting: false,
    };
  },
  computed: {
    noteId() {
      return Number(this.$route?.params?.simpleNoteId || 0) || 0;
    },
    headerTitle() {
      return String(this.text?.detail?.page?.title || 'Nota simple').trim() || 'Nota simple';
    },
    headerActions() {
      return [];
    },
    simpleNoteReference() {
      const serial = String(this.note?.serial || 'NS').trim();
      const folio = String(this.note?.folio || '').trim();
      return `${serial}${folio}`.trim() || '-';
    },
    breadcrumbItems() {
      return [
        {
          label: String(this.text?.detail?.breadcrumb?.root || 'Notas simples').trim() || 'Notas simples',
          to: { name: 'simpleNotes' },
        },
        {
          label: this.simpleNoteReference,
        },
      ];
    },
    breadcrumbAriaLabel() {
      return String(this.text?.detail?.breadcrumb?.ariaLabel || 'Ruta de nota simple').trim() || 'Ruta de nota simple';
    },
    breadcrumbBackLabel() {
      return String(this.text?.detail?.breadcrumb?.backLabel || 'Volver').trim() || 'Volver';
    },
    simpleNoteHeaderTitle() {
      const title = String(this.text?.detail?.page?.title || 'Nota simple').trim() || 'Nota simple';
      return `${title} ${this.simpleNoteReference}`.trim();
    },
    simpleNoteIsCanceled() {
      return Number(this.note?.canceled || 0) === 1;
    },
    simpleNoteStatusLabel() {
      const fallback = this.simpleNoteIsCanceled
        ? String(this.text?.detail?.status?.canceled || 'CANCELADA').trim()
        : String(this.text?.detail?.status?.active || 'VIGENTE').trim();
      return String(this.note?.status || fallback).trim() || fallback;
    },
    primaryHeaderActions() {
      const actions = [];

      if (this.showPayAction) {
        actions.push({
          key: 'pay',
          label: String(this.text?.table?.actions?.pay || 'Pagar').trim() || 'Pagar',
          icon: 'fa-credit-card',
          variant: 'success',
        });
      }

      actions.push({
        key: 'downloadPdf',
        label: String(this.text?.detail?.headerActions?.downloadPdfShort || 'PDF').trim() || 'PDF',
        icon: 'fa-file-pdf-o',
        variant: 'secondary',
      });

      actions.push({
        key: 'send',
        label: String(this.text?.detail?.headerActions?.send || 'Enviar').trim() || 'Enviar',
        icon: 'fa-paper-plane-o',
        variant: 'primary',
      });

      return actions;
    },
    secondaryHeaderActions() {
      return [
        {
          key: 'cancel',
          label: String(this.text?.detail?.headerActions?.cancel || 'Cancelar').trim() || 'Cancelar',
          icon: 'fa-ban',
          variant: 'ghost',
        },
      ];
    },
    detailTabs() {
      const labels = this.text?.detail?.tabs || {};

      return [
        { key: 'detail', label: String(labels.detail || 'Detalle').trim() || 'Detalle' },
        { key: 'shipping', label: String(labels.shipping || 'Envíos').trim() || 'Envíos' },
        { key: 'documents', label: String(labels.documents || 'Documentos').trim() || 'Documentos' },
        { key: 'history', label: String(labels.history || 'Historial').trim() || 'Historial' },
      ];
    },
    sectionTabsAriaLabel() {
      return String(this.text?.detail?.sectionTabsAriaLabel || 'Secciones del detalle de nota simple').trim()
        || 'Secciones del detalle de nota simple';
    },
    noteConcepts() {
      return Array.isArray(this.note?.concepts) ? this.note.concepts : [];
    },
    noteComments() {
      return String(this.note?.comments || '').trim();
    },
    noteCurrency() {
      return String(this.note?.currency || 'MXN').trim().toUpperCase() || 'MXN';
    },
    noteSubtotal() {
      return Number(this.note?.subtotal || 0) || 0;
    },
    noteTotal() {
      return Number(this.note?.total || 0) || 0;
    },
    showPayAction() {
      const statusKey = String(this.note?.status_key || this.note?.status || '').trim().toLowerCase();
      const hasRelatedSale = Number(this.note?.sale_id || 0) > 0 || this.note?.has_sale === true;
      return hasRelatedSale && !['paid', 'done', 'pagada', 'cobrada', 'canceled', 'cancelada'].includes(statusKey);
    },
    operationData() {
      const paymentCodes = Array.isArray(this.note?.payment_receipt_codes)
        ? this.note.payment_receipt_codes
          .map((value) => String(value || '').trim())
          .filter((value) => value !== '')
        : [];

      return {
        statusKey: String(this.note?.status_key || '').trim().toLowerCase() || 'pending',
        statusLabel: String(this.note?.status || '').trim() || this.simpleNoteStatusLabel,
        balance: Number(this.note?.balance || 0) || 0,
        currency: this.noteCurrency,
        dueDate: String(this.note?.date || '').trim(),
        customerName: String(this.note?.customer_name || this.note?.company_name || '').trim(),
        customerId: Number(this.note?.customer_id || this.note?.company_id || 0) || 0,
        saleName: String(this.note?.sale_name || this.note?.business_name || '').trim(),
        saleCode: String(this.note?.sale_code || '').trim(),
        saleId: Number(this.note?.sale_id || 0) || 0,
        isSent: String(this.note?.last_sent_at || this.note?.sent_at || '').trim() !== '',
        sentAt: String(this.note?.sent_at || this.note?.date || '').trim(),
        lastSentAt: String(this.note?.last_sent_at || '').trim(),
        paymentReceiptId: Number(this.note?.payment_receipt_id || 0) || 0,
        paymentReceiptCode: String(this.note?.payment_receipt_code || this.note?.payment?.code || '').trim(),
        paymentReceiptCodesText: String(this.note?.payment_receipt_codes_text || paymentCodes.join(', ')).trim(),
        relatedPaymentLabel: '',
      };
    },
    sendEmailContextData() {
      const simpleNoteData = this.note && typeof this.note === 'object'
        ? this.note
        : {};

      return {
        id: this.noteId,
        total: simpleNoteData?.total,
        customerId: simpleNoteData?.customer_id,
        customerName: String(simpleNoteData?.customer_name || simpleNoteData?.company_name || '').trim(),
        currency: String(simpleNoteData?.currency || '').trim(),
        reference: this.simpleNoteReference,
        simpleNoteData,
      };
    },
    cancelConfirmTitle() {
      return String(this.text?.detail?.cancelConfirm?.title || 'Cancelar nota simple').trim() || 'Cancelar nota simple';
    },
    cancelConfirmSubtitle() {
      return String(this.text?.detail?.cancelConfirm?.subtitle || 'Confirma para continuar con esta operación.').trim()
        || 'Confirma para continuar con esta operación.';
    },
    cancelConfirmMessage() {
      return String(this.text?.detail?.cancelConfirm?.message || 'La nota simple se cancelará y sus planes de pago volverán a pendiente. Esta acción no se puede deshacer.').trim()
        || 'La nota simple se cancelará y sus planes de pago volverán a pendiente. Esta acción no se puede deshacer.';
    },
    cancelConfirmButton() {
      return String(this.text?.detail?.cancelConfirm?.confirm || 'Sí, cancelar nota').trim() || 'Sí, cancelar nota';
    },
    cancelConfirmDismiss() {
      return String(this.text?.detail?.cancelConfirm?.cancel || 'Volver').trim() || 'Volver';
    },
  },
  async created() {
    await this.loadDetail();
    document.addEventListener('click', this.handleGlobalClick, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleGlobalClick, true);
  },
  methods: {
    async loadDetail() {
      if (this.noteId <= 0) {
        this.note = null;
        return;
      }

      this.loading = true;
      try {
        const response = await simpleNotesService.show(this.noteId);
        const payload = extractPayload(response);
        const data = payload?.data || payload || {};
        this.note = data && typeof data === 'object' ? data : null;
      } catch (error) {
        this.note = null;
        this.$toast?.error(String(this.text?.detail?.toast?.loadError || 'No fue posible consultar la nota simple.'));
      } finally {
        this.loading = false;
      }
    },
    toggleMoreActionsMenu() {
      this.showMoreActionsMenu = !this.showMoreActionsMenu;
    },
    handleGlobalClick(event) {
      if (!this.showMoreActionsMenu) {
        return;
      }

      const target = event?.target;
      if (!target) {
        return;
      }

      if (target.closest('.app-detail-more-actions-wrap')) {
        return;
      }

      this.showMoreActionsMenu = false;
    },
    handlePrimaryHeaderAction(action, onAction) {
      this.showMoreActionsMenu = false;
      if (typeof onAction === 'function') {
        onAction(action);
      }
    },
    handleSecondaryHeaderAction(action, onAction) {
      this.showMoreActionsMenu = false;
      if (typeof onAction === 'function') {
        onAction(action);
      }
    },
    async onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim();

      if (actionKey === 'pay') {
        this.openPayNoteModal();
        return;
      }

      if (actionKey === 'downloadPdf') {
        await this.downloadSimpleNotePdf();
        return;
      }

      if (actionKey === 'send') {
        this.openSendEmailModal();
        return;
      }

      if (actionKey === 'cancel') {
        this.openCancelConfirmModal();
        return;
      }
    },
    openCancelConfirmModal() {
      if (this.noteId <= 0 || this.simpleNoteIsCanceled) {
        return;
      }

      this.showCancelConfirmModal = true;
    },
    closeCancelConfirmModal() {
      if (this.cancelNoteSubmitting) {
        return;
      }

      this.showCancelConfirmModal = false;
    },
    async confirmCancelSimpleNote() {
      if (this.cancelNoteSubmitting || this.noteId <= 0) {
        return;
      }

      this.cancelNoteSubmitting = true;
      try {
        const response = await simpleNotesService.cancel(this.noteId, {});
        const apiErrorMessage = extractApiErrorMessage(response, 'No fue posible cancelar la nota simple.');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, 'Nota simple cancelada correctamente.'));
        this.showCancelConfirmModal = false;
        await this.loadDetail();
        this.noteShippingRefreshTick += 1;
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || 'No fue posible cancelar la nota simple.');
      } finally {
        this.cancelNoteSubmitting = false;
      }
    },
    buildSimpleNoteDocumentBaseName() {
      const reference = String(this.simpleNoteReference || '').trim();
      const noteId = Number(this.noteId || 0) || 0;
      const fallback = noteId > 0 ? `nota_simple_${noteId}` : 'nota_simple';

      return (reference !== '' ? reference : fallback)
        .replace(/\s+/g, '_')
        .replace(/[^A-Za-z0-9_\-]/g, '') || fallback;
    },
    async downloadSimpleNotePdf() {
      if (this.noteId <= 0) {
        this.$toast?.error(String(this.text?.detail?.toast?.loadError || 'No fue posible consultar la nota simple.'));
        return;
      }

      try {
        const response = await simpleNotesService.downloadPdf(this.noteId);
        this.openPdfBlob(response?.data ?? response);
      } catch (error) {
        const fallback = String(this.text?.detail?.toast?.actions?.download || 'No fue posible descargar el PDF de la nota simple.');
        this.$toast?.error(extractApiErrorMessage(error, fallback));
      }
    },
    openPdfBlob(payload) {
      const blob = payload instanceof Blob
        ? payload
        : new Blob([payload], { type: 'application/pdf' });

      const objectUrl = window.URL.createObjectURL(blob);
      const opened = window.open(objectUrl, '_blank', 'noopener');

      if (!opened) {
        const fileName = `${this.buildSimpleNoteDocumentBaseName()}.pdf`;
        const link = window.document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        link.click();
      }

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 60 * 1000);
    },
    buildPayNoteModalRow() {
      const row = this.note || {};
      const saleId = Number(row?.sale_id || 0) || 0;
      if (saleId <= 0 && row?.has_sale !== true) {
        return null;
      }

      const paymentId = Number(row?.payment_id || row?.payment?.id || 0) || 0;
      const customerId = Number(row?.customer_id || row?.company_id || 0) || 0;
      const total = Number(row?.balance || row?.total || 0) || 0;

      if (paymentId <= 0 || customerId <= 0 || total <= 0) {
        return null;
      }

      return {
        id: paymentId,
        customer_id: customerId,
        company_id: customerId,
        customer_name: String(row?.customer_name || row?.company_name || '').trim(),
        company_name: String(row?.company_name || row?.customer_name || '').trim(),
        date: String(row?.date || '').trim(),
        currency: this.noteCurrency,
        exchange_rate: Number(row?.exchange_rate || 1) || 1,
        total,
        subtotal: total,
        simple_note_id: Number(row?.id || 0) || 0,
        invoice: this.simpleNoteReference,
        voucher_type: 'simple_note',
        special: 1,
      };
    },
    openPayNoteModal() {
      const modalRow = this.buildPayNoteModalRow();
      if (!modalRow) {
        this.$toast?.error('No se puede pagar una nota simple sin negocio relacionado.');
        return;
      }

      this.payNoteRow = modalRow;
      this.showPayNoteModal = true;
    },
    closePayNoteModal(force = false) {
      if (this.payNoteSubmitting && !force) {
        return;
      }

      this.showPayNoteModal = false;
      this.payNoteRow = null;
    },
    async submitPayNoteModal(payload) {
      this.payNoteSubmitting = true;

      try {
        const simpleNoteId = Number(this.payNoteRow?.simple_note_id || this.note?.id || 0) || 0;
        if (simpleNoteId <= 0) {
          this.$toast?.error('No se pudo identificar la nota simple para registrar el cobro.');
          return;
        }

        const response = await paymentReceiptsService.store({
          ...payload,
          voucher_type: 'simple_note',
          special: 1,
          simple_note_id: simpleNoteId,
          payment_plan_id: 0,
          should_stamp_payment: 0,
          stamp_payment: 0,
          timbrar: 0,
        });
        const apiErrorMessage = extractApiErrorMessage(response, 'No fue posible registrar el pago de la nota simple.');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, 'Pago de nota simple registrado correctamente.'));
        this.closePayNoteModal(true);
        await this.loadDetail();
        this.noteShippingRefreshTick += 1;
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || error?.message || 'No fue posible registrar el pago de la nota simple.');
      } finally {
        this.payNoteSubmitting = false;
      }
    },
    ...invoiceDetailSendEmailMethods,
  },
};
</script>

<style scoped>
.invoice-detail-header-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.invoice-detail-content-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.invoice-detail-heading-wrap {
  align-items: center;
}

.invoice-detail-heading-content {
  width: 100%;
}

.invoice-detail-heading-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.invoice-detail-heading-title {
  margin: 0;
}

.invoice-detail-header-btn--icon-only {
  min-width: 36px;
  padding-left: 10px;
  padding-right: 10px;
}

.invoice-detail-header-btn--download-bundle {
  background: rgba(31, 42, 61, 0.1);
  color: #1f2a3d;
  border: 1px solid rgba(31, 42, 61, 0.2);
}

.invoice-detail-header-btn--download-bundle:not(:disabled):hover {
  background: rgba(31, 42, 61, 0.16);
}

.invoice-detail-more-actions-menu__item--critical {
  color: #b91c1c;
}

.invoice-detail-more-actions-menu__item--critical:hover {
  background: rgba(185, 28, 28, 0.08);
  color: #991b1b;
}

.simple-note-detail-loading {
  padding: 16px;
}

@media (max-width: 767px) {
  .invoice-detail-header-left {
    gap: 6px;
  }

  .invoice-detail-heading-main {
    gap: 8px;
  }
}
</style>

